import { PayPalButtonsComponent } from '@paypal/paypal-js';

import {
  groupLineItems,
  flattenLineItemAdapter,
  calculateFreightAmount,
  calculateDiscountAmount,
} from 'businessLogic/server/scsToIcnAdapter.js';
import { LocalStorageUtil } from 'businessLogic/utils/storage';
import HttpClient from 'server/helpers/HttpClient';
import { getUUID } from 'server/helpers/uuidGenerator';
import { formatMaskedAccountNumber, formatMaskedCardNumber } from 'shared/PaymentHelpers';
import { getWeakTicket } from 'shared/clientUtils';
import { insightSelectors } from 'store/insight/selectors';
import { RootState } from 'store/store';
import {
  AdyenPaymentMethod,
  PaymentOrderData,
  PaymentOrderResponse,
  BrowserInfo,
} from 'types/BusinessLogic';
import { TXN_MAP, SALE_MAP } from 'types/constants';
import { Nullable, ValueOf } from 'types/utils';

const httpClient = HttpClient.getInstance();
const { SplunkReporter } = require('reporting/splunk/SplunkReporter');
const splunkReporter = SplunkReporter.getInstance();
const logger = 'businessLogic/Payment/Common';
type BaseReqParams = {
  bioCatchSessionId: string;
  paymentMethod: string;
  maskedPayment: string;
  amountPaid: number;
  riskProfileToken: string;
  paymentProcessor: string;
  saleId: number;
  freight: number;
  discount: {
    amount?: number;
  };
  tax: {
    totalTaxAmount?: number;
  };
  lines: [];
  saleType: string;
  currency: string;
  customerName: string;
};

type SubmitPaymentParams = {
  data: Object;
  portal: string;
  ssrtid: string;
  domainId: string;
  token: string;
  ticket: string;
  realmId: string;
  isUserSignedIn: boolean;
  authToken: string;
  syncToken: string;
  entityId: string;
  timeout: number;
};

type LineItem = {
  code?: number;
  productCode?: number;
  totalAmount?: number;
  amount?: number;
  unitPrice?: number;
  type: ValueOf<typeof SALE_MAP.LINE_ITEM_TYPE>;
};

interface paymentSuccessParams {
  paymentMethodType: string;
  successfulPaymentCounter: number;
  paymentSessionCounter: number;
  payload: {
    result: {
      config: {
        headers: {
          intuit_tid: string;
        };
      };
      data: {
        maskedAccountNumber: string;
        maskedCardNumber: string;
        cardType: string;
        bankShortLivedToken?: string;
        cardShortLivedToken?: string;
        successUrl?: string;
        failureUrl?: string;
      };
    };
  };
}

interface FlattenedLineItem {
  description: string;
  amount?: number;
  totalAmount?: number;
  discountAmount?: number;
  productCode?: string;
  code?: string;
  unitPrice?: number;
  quantity: number;
  unitOfMeasure: string;
}

type SCSLineItems = Array<LineItem>;

const getBaseRequestBody = ({
  paymentMethod,
  maskedPayment,
  amountPaid,
  riskProfileToken,
  paymentProcessor,
  saleId,
  freight,
  tax,
  lines,
  saleType,
  currency,
  customerName,
  bioCatchSessionId,
}: BaseReqParams) => {
  return {
    paymentBasicSaleInfo: {
      saleId,
      saleType,
      amountPaid: amountPaid.toFixed(2),
      paymentMethod,
      paymentProcessor,
      customerName,
      currency,
      riskProfileToken,
      maskedPayment,
      bioCatchSessionId,
    },
    saleInfo: {
      id: saleId,
      freightAmount: calculateFreightAmount(freight),
      discountAmount: calculateDiscountAmount(lines),
      taxAmount: tax || 0,
      lines: convertLineItemsToSCSPaymentFormat(groupLineItems(lines)),
    },
  };
};

const submitPayment = async ({
  data,
  portal,
  ssrtid,
  domainId,
  token,
  ticket,
  realmId,
  isUserSignedIn,
  authToken,
  syncToken,
  entityId,
  timeout,
}: SubmitPaymentParams) => {
  const headers = getWeakTicket({
    domainId,
    entityId,
    realmId,
    token,
    ticket,
    isUserSignedIn,
    syncToken,
    authToken,
    ssrtid,
  });
  //@ts-ignore
  const endpoint =
    //@ts-ignore
    data?.paymentBasicSaleInfo?.ccSurchargeFeeAmountEnabled
      ? `/${portal}/rest/pay/v2`
      : `/${portal}/rest/pay`;

  const result = await httpClient({
    method: 'POST',
    headers,
    data,
    timeout,
    url: endpoint,
    endpoint: endpoint,
    event: 'pay',
  });
  return result;
};

const generatePaymentSucessPayload = ({
  payload,
  paymentMethodType,
  successfulPaymentCounter,
  paymentSessionCounter,
}: paymentSuccessParams) => {
  let { data, config: { headers: { intuit_tid = '' } = {} } = {} } =
    (payload && payload.result) || {};

  data = { ...data };
  if (paymentMethodType !== 'ap') {
    if (data && data.maskedAccountNumber) {
      // Transform the bankAccount to the proper format.
      data.maskedAccountNumber = formatMaskedAccountNumber(data.maskedAccountNumber);
    } else if (data && data.maskedCardNumber) {
      // If it's credit card, then format it and put it into data so the Success Screen can show it.
      data.maskedAccountNumber = formatMaskedCardNumber(data.cardType, data.maskedCardNumber);
    }
  }

  let successRedirectDetails = {};
  if (data?.successUrl) {
    successRedirectDetails = {
      successRedirectTimeoutInSec: 10,
      successRedirectUrl: data.successUrl,
    };
  }

  return {
    paymentStatus: TXN_MAP.STATUS.SUCCESS,
    paymentError: null,
    ...successRedirectDetails,
    paymentSessionCounter: paymentSessionCounter + 1,
    successfulPaymentCounter: successfulPaymentCounter + 1,
    lastSuccessfulPaymentResponse: { ...data, intuit_tid },
  };
};
const isPaypalAppconnectDeclinedOrBlocked = (
  featureFlags: Record<string, any>,
  paymentStatus: Nullable<ValueOf<typeof TXN_MAP.STATUS>>,
  payPalProcessor: string
) => {
  return !!(
    featureFlags &&
    featureFlags['block-second-payments-paypal-appconnect'] &&
    (paymentStatus === TXN_MAP.STATUS.ERROR || paymentStatus === TXN_MAP.STATUS.DECLINED) &&
    payPalProcessor === 'APPCONNECT'
  );
};
const closeAllPayPalButtons = (paymentMethodType: string, newPaymentMethodType: string) => {
  const instances: PayPalButtonsComponent[] =
    (typeof window !== 'undefined' &&
      window.paypal &&
      window.paypal.Buttons &&
      // @ts-ignore private API?
      window.paypal.Buttons.instances) ||
    [];
  if (
    instances &&
    ['paypal_ppaam', 'paypal', 'venmo', 'pp'].includes(paymentMethodType) &&
    paymentMethodType !== newPaymentMethodType
  ) {
    instances.map((button: PayPalButtonsComponent) => {
      return button.close().catch((e: any) => {
        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'pay',
          action: 'closeButtons',
          activityInfo: {
            status: 'error',
          },
          error: {
            message: e.message,
            stack: e.stack,
          },
        });
      });
    });
  }
};

const isCardPaymentMethod = (paymentMethodType: string) => {
  return !!(
    paymentMethodType === 'cc' ||
    paymentMethodType === 'dc' ||
    paymentMethodType === 'dc,cc'
  );
};
const convertLineItemsToSCSPaymentFormat = (lineItems: SCSLineItems, isAddL3Fields?: boolean) => {
  try {
    return lineItems.map((lineItem: LineItem) => {
      const flattenedLineItem = flattenLineItemAdapter(
        lineItem,
        isAddL3Fields
      ) as FlattenedLineItem;
      // next if is a dirty fix since SCS need an indicator for a discount field
      // in estimate - discount field is negative. in invoice it's positive
      if (lineItem.type === SALE_MAP.LINE_ITEM_TYPE.DISCOUNT_LINE_DETAIL) {
        flattenedLineItem.discountAmount = flattenedLineItem.amount;
      }
      flattenedLineItem.code = flattenedLineItem.productCode;
      delete flattenedLineItem.productCode;
      flattenedLineItem.totalAmount = flattenedLineItem.amount;
      delete flattenedLineItem.amount;
      flattenedLineItem.amount = flattenedLineItem.unitPrice;
      delete flattenedLineItem.unitPrice;
      return flattenedLineItem;
    });
  } catch (e: any) {
    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'pay',
      action: 'convertLineItems',
      activityInfo: {
        status: 'error',
      },
      error: {
        message: e.message,
        stack: e.stack,
      },
    });
    return lineItems;
  }
};

const handlePaymentOrder = async (
  state: RootState,
  paymentMethod: AdyenPaymentMethod,
  paymentDetails: {
    payorName: string;
    payorEmailAddress: string;
    country: string;
    [key: string]: any;
  }
) => {
  const { payment, sale, config, auth, insight } = state;
  const { amount } = payment;
  const {
    type,
    isGpu,
    currencyInfo: { currency },
  } = sale;
  const { domainId, token } = insight;
  const { portal, ssrtid } = config;
  const { ticket, realmId, isUserSignedIn, authToken, syncToken, entityId } = auth;

  if (amount < 1) {
    throw new Error('cannot pay amount < 1');
  }

  const headers = getWeakTicket({
    domainId,
    entityId,
    realmId,
    token,
    ticket,
    isUserSignedIn,
    syncToken,
    authToken,
    ssrtid,
  });

  const data: PaymentOrderData = {
    payment: {
      method: paymentMethod,
      amount: amount.toFixed(2),
      currencyCode: currency,
    },
    sale: {
      type,
      isGpu: isGpu ?? false,
    },
    contact: {
      emails: [paymentDetails.payorEmailAddress],
    },
    risk: {
      profileToken: insightSelectors.riskProfileTokenSelector(insight),
    },
  };

  if (paymentMethod === AdyenPaymentMethod.PAYBYBANK) {
    data.payByBankDetail = {
      issuerBankId: paymentDetails.issuerBankId,
      issuerBankName: paymentDetails.issuerBankName,
      payorName: paymentDetails.payorName,
      payorEmailAddress: paymentDetails.payorEmailAddress,
      country: paymentDetails.country,
    };
  } else {
    // PAYBYCARD or PAYBYCARD_WALLET
    const browserInfo = getBrowserInfo();
    data.creditCardDetail = {
      payorName: paymentDetails.payorName,
      country: paymentDetails.country,
      walletInfo: paymentDetails?.walletInfo,
      browserInfo: browserInfo,
      isSavePaymentMethodChecked: paymentDetails?.isSavePaymentMethodChecked,
      entityId: entityId,
      ticket: ticket,
    };
  }

  headers['Authorization'] = `Bearer ${authToken}`;
  const idempotencyKey = `init_pay_idempotency_${token}_${amount}`;
  const newIdempotencyValue = getUUID();
  const idempotencyValue = LocalStorageUtil.getOrSetStorage(idempotencyKey, newIdempotencyValue);
  headers['intuit_tid'] = idempotencyValue;

  const response: PaymentOrderResponse = await httpClient({
    url: `/${portal}/rest/adyen/create-order`,
    endpoint: `/${portal}/rest/adyen/create-order`,
    method: 'POST',
    data,
    headers,
    timeout: 60000,
  });
  return response.data;
};

const cleanupIdempotencyKey = (token: string, amount: number): void => {
  const idempotencyKey = `init_pay_idempotency_${token}_${amount}`;
  LocalStorageUtil.removeFromStorage(idempotencyKey);
};

export const invalidateAdyenIdempotencyKey = ({
  payment: { paymentStatus, paymentMethodType, inputAmount } = {
    paymentStatus: '',
    paymentMethodType: '',
    inputAmount: '',
  },
  insight: { token } = { token: '' },
}) => {
  const isPbcOrPbb = paymentMethodType === 'pbc' || paymentMethodType === 'pbb';
  if (isPbcOrPbb && paymentStatus) {
    cleanupIdempotencyKey(token, Number(inputAmount));
  }
};

const getBrowserInfo = (): BrowserInfo => {
  const defaultBrowserInfo = {
    userAgent: '',
    acceptHeader: '',
    language: '',
    colorDepth: 0,
    screenHeight: 0,
    screenWidth: 0,
    timeZoneOffset: 0,
    javaEnabled: false,
    javaScriptEnabled: false,
  };

  if (typeof window === 'undefined') {
    return defaultBrowserInfo;
  }

  try {
    return {
      userAgent: navigator.userAgent || '',
      acceptHeader:
        'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8',
      language: navigator.language || '',
      colorDepth: screen.colorDepth || 24,
      screenHeight: screen.height || 0,
      screenWidth: screen.width || 0,
      timeZoneOffset: new Date().getTimezoneOffset(),
      javaEnabled: false,
      javaScriptEnabled: true,
    };
  } catch {
    return defaultBrowserInfo;
  }
};

export {
  generatePaymentSucessPayload,
  getBaseRequestBody,
  submitPayment,
  convertLineItemsToSCSPaymentFormat,
  isPaypalAppconnectDeclinedOrBlocked,
  closeAllPayPalButtons,
  isCardPaymentMethod,
  handlePaymentOrder,
  getBrowserInfo,
  cleanupIdempotencyKey,
};
